"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useCookies } from "react-cookie";
import { useMutation } from "@tanstack/react-query";
import { LogOut, Menu, User, X } from "lucide-react";
import { handleLogOut } from "@/api/auth";
import { cn } from "@/lib/utils";
import { EVENT, KV, NAV_LINKS } from "@/components/home/data";

interface SiteNavProps {
  /** "hero" sits transparently inside the blue home hero; "sticky" is the fixed blue bar of inner pages. */
  variant?: "hero" | "sticky";
}

/** Single navigation bar shared by the home hero and every inner page. */
const SiteNav: React.FC<SiteNavProps> = ({ variant = "sticky" }) => {
  const router = useRouter();
  const path = usePathname();
  const [cookies, , removeCookie] = useCookies(["access_token", "gender"]);
  const [hasToken, setHasToken] = useState(false);
  const [open, setOpen] = useState(false);

  const logoutMutation = useMutation({ mutationKey: ["handleLogOut"], mutationFn: handleLogOut });

  useEffect(() => {
    setHasToken(!!cookies.access_token);
  }, [cookies.access_token]);

  useEffect(() => {
    setOpen(false);
  }, [path]);

  const logOut = () => {
    if (!cookies.access_token) return;
    logoutMutation.mutate(cookies.access_token);
    removeCookie("access_token", { path: "/" });
    removeCookie("gender", { path: "/" });
    router.push("/login");
  };

  const links = NAV_LINKS;
  const isActive = (href: string) => (href === "/" ? path === "/" : path.startsWith(href));

  const cta = hasToken ? (
    <>
      <Link
        href="/profile"
        className="mes-label flex items-center gap-2 border border-white/60 px-3 py-2.5 hover:bg-white hover:text-brand-blue transition-colors"
      >
        <User className="w-4 h-4" /> Perfil
      </Link>
      <button
        onClick={logOut}
        data-leaves-page
        className="mes-label flex items-center gap-2 bg-white text-brand-blue px-3 py-2.5 hover:bg-brand-yellow hover:text-brand-ink transition-colors"
      >
        <LogOut className="w-4 h-4" /> Salir
      </button>
    </>
  ) : (
    <Link
      href="/login"
      className="mes-label bg-white text-brand-blue px-4 py-2.5 hover:bg-brand-yellow hover:text-brand-ink transition-colors whitespace-nowrap"
    >
      Inscríbete →
    </Link>
  );

  return (
    <header
      className={cn(
        "text-white",
        variant === "sticky" ? "sticky top-0 z-50 bg-brand-blue" : "relative z-30"
      )}
    >
      <div
        className={cn(
          "px-4 md:px-8 flex items-center justify-between gap-6 border-b border-white/25",
          variant === "sticky" ? "h-16 md:h-[72px]" : "pt-4 md:pt-5 pb-4 md:pb-5"
        )}
      >
        <Link href="/" className="block w-36 md:w-44 shrink-0" aria-label={EVENT.name}>
          <Image
            src={KV.lettering}
            alt={EVENT.name}
            width={2400}
            height={548}
            priority
            sizes="176px"
            className="w-full h-auto drop-shadow-[0_2px_0_rgba(0,0,0,0.25)]"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8 mes-label">
          {links.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className={cn("flex items-center gap-2 hover:text-brand-yellow transition-colors", isActive(l.href) && "text-brand-yellow")}
            >
              <span className={cn("w-1.5 h-1.5 rounded-full bg-current", isActive(l.href) ? "opacity-100" : "opacity-0")} />
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">{cta}</div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden mes-label flex items-center gap-2 border border-white/60 px-3 h-11"
          aria-label="Abrir menú"
        >
          Menú {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          "md:hidden fixed inset-0 z-40 bg-brand-blue px-6 pt-24 pb-10 flex flex-col transition-transform duration-300",
          open ? "translate-y-0" : "-translate-y-full pointer-events-none"
        )}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute right-4 top-4 mes-label flex items-center gap-2 border border-white/60 px-3 h-11"
        >
          Cerrar <X className="w-4 h-4" />
        </button>
        <nav className="flex flex-col gap-4">
          {[...links, ...(hasToken ? [{ label: "Perfil", href: "/profile" }] : [])].map((l) => (
            <Link
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              aria-current={isActive(l.href) ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 font-label uppercase tracking-[0.14em] text-2xl hover:text-brand-yellow",
                isActive(l.href) && "text-brand-yellow"
              )}
            >
              <span className={cn("w-2 h-2 rounded-full bg-current shrink-0", isActive(l.href) ? "opacity-100" : "opacity-0")} />
              {l.label}
            </Link>
          ))}
          {hasToken ? (
            <button
              onClick={() => {
                setOpen(false);
                logOut();
              }}
              data-leaves-page
              className="flex items-center gap-3 font-label uppercase tracking-[0.14em] text-2xl text-left hover:text-brand-yellow"
            >
              <span className="w-2 h-2 shrink-0" />
              Salir
            </button>
          ) : (
            <Link
              href="/login"
              onClick={() => setOpen(false)}
              className="mes-label self-start mt-4 bg-white text-brand-blue px-4 py-3 hover:bg-brand-yellow hover:text-brand-ink transition-colors"
            >
              Inscríbete →
            </Link>
          )}
        </nav>
        <div className="mt-auto mes-label space-y-1">
          <p>{EVENT.dateLong}</p>
          <p>{EVENT.place}</p>
          <a href={`mailto:${EVENT.email}`}>{EVENT.email}</a>
        </div>
      </div>
    </header>
  );
};

export default SiteNav;
